// OrderDetailsTab.js
import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Space, Tooltip, Tag, Radio, Divider, Dropdown } from 'antd';
import { 
  InfoCircleOutlined, BarChartOutlined, DownOutlined, RightOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { 
  prepareOrderDetailsData, 
  formatCurrency, 
  STATUS_TYPES 
} from '../../../utils/dataUtils';

/**
 * Order Details Tab component for displaying order data
 * @param {Object} props - Component props
 * @param {Array} props.reportData - Processed report data
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.showPricesWithoutVAT - Whether to show prices without VAT
 * @param {Object} props.exportMenu - Export dropdown menu
 * @param {Function} props.onViewModeChange - Callback when view mode changes
 * @param {boolean} props.isClient - Whether the current user is a client user
 */
const OrderDetailsTab = ({ 
  reportData, 
  loading, 
  showPricesWithoutVAT,
  exportMenu,
  onViewModeChange,
  isClient = false
}) => {
  // Single viewMode state: 'default', 'items', or 'grouped'
  const [viewMode, setViewMode] = useState('default');
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [tableData, setTableData] = useState({
    dataSource: [],
    groupedData: null
  });

  // Success and Failed status mappings
  const successStatuses = STATUS_TYPES.SUCCESS;
  const failedStatuses = STATUS_TYPES.FAILED;

  // Notify parent component when view mode changes
  useEffect(() => {
    if (onViewModeChange) {
      onViewModeChange(viewMode);
    }
  }, [viewMode, onViewModeChange]);

  // Process order data whenever reportData or view mode changes
  useEffect(() => {
    if (reportData && reportData.length > 0) {
      const data = prepareOrderDetailsData(reportData, viewMode);
      setTableData(data);
    }
    else {
      setTableData({
        dataSource: [],
        groupedData: null
      });
    }
  }, [reportData, viewMode]);

  // Calculate total sum for the table footer
  const getTotalOrderSum = () => {
    if (viewMode === 'grouped') {
      return tableData.dataSource.reduce((sum, group) => {
        return sum + (group.totalRevenue || 0);
      }, 0);
    }
    
    return reportData.reduce((sum, order) => {
      return sum + parseFloat(order.order_total || 0);
    }, 0);
  };
  
  // Calculate total VAT amount for the table footer
  const getTotalVATSum = () => {
    if (viewMode === 'grouped') {
      return tableData.dataSource.reduce((sum, group) => {
        return sum + (group.totalVAT || 0);
      }, 0);
    }
    
    return reportData.reduce((sum, order) => {
      return sum + parseFloat(order.vat_amount || 0);
    }, 0);
  };
  
  // Create table summary row
  const tableSummary = () => {
    // Calculate the colspan based on view mode and whether VAT column is showing
    const baseColSpan = viewMode === 'grouped' ? 7 : 8;
    const totalColSpan = showPricesWithoutVAT ? baseColSpan : baseColSpan + 1;
    
    return (
      <>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={totalColSpan} className="text-right font-bold">
            {showPricesWithoutVAT ? 'Total (excl. VAT):' : 'Total:'}
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} className="font-bold text-right">
            {formatCurrency(getTotalOrderSum())}
          </Table.Summary.Cell>
          
          {/* VAT amount summary cell */}
          {showPricesWithoutVAT && (
            <Table.Summary.Cell index={2} className="font-bold text-right">
              {formatCurrency(getTotalVATSum())}
            </Table.Summary.Cell>
          )}
          
          <Table.Summary.Cell index={showPricesWithoutVAT ? 3 : 2}>
            {viewMode === 'default' && ' '}
          </Table.Summary.Cell>
        </Table.Summary.Row>
        
        {/* Additional row for total including VAT */}
        {showPricesWithoutVAT && (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={totalColSpan} className="text-right font-bold">
              Total (incl. VAT):
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1} className="font-bold text-right">
              {formatCurrency(getTotalOrderSum() + getTotalVATSum())}
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2} className="font-bold text-right">
              
            </Table.Summary.Cell>
            <Table.Summary.Cell index={3}>
              {viewMode === 'default' && ' '}
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      </>
    );
  };

  // Base table columns
  const getBaseColumns = () => {
    const columns = [
      {
        title: 'Order ID',
        dataIndex: 'id',
        key: 'id',
        sorter: (a, b) => a.id - b.id,
        width: 100
      },
      {
        title: 'Date',
        dataIndex: 'property_date',
        key: 'property_date',
        render: (date) => new Date(date).toLocaleDateString() ?? '',
        sorter: (a, b) => new Date(a.property_date) - new Date(b.property_date),
        width: 120
      },
      {
        title: 'Time Booked',
        key: 'time_booked',
        render: (_, record) => {
          // Extract time booked
          let arrivalTime = null;
          let completionTime = null;
          
          if (record.order_products && record.order_products.length > 0) {
            arrivalTime = record.order_products[0].order_property_arrival_time || null;
            completionTime = record.order_products[0].order_property_completion_time || null;
          }
          
          if (!arrivalTime && record.order_property) {
            arrivalTime = record.order_property.arrival_time || null;
            completionTime = record.order_property.completion_time || null;
          }
          
          const timeBooked = arrivalTime ? `${arrivalTime} - ${completionTime || ' '}` : ' ';
          return timeBooked;
        },
        width: 150
      },
      {
        title: 'Customer',
        key: 'customer',
        render: (_, record) => `${record.delivery_first_name || ''} ${record.delivery_last_name || ''}`,
        sorter: (a, b) => {
          const nameA = `${a.delivery_first_name} ${a.delivery_last_name}`;
          const nameB = `${b.delivery_first_name} ${b.delivery_last_name}`;
          return nameA.localeCompare(nameB);
        },
        width: 160
      },
      {
        title: 'Property',
        key: 'property',
        render: (_, record) => record.order_property?.property_title || ' ',
        ellipsis: true,
        width: 160
      },
      {
        title: 'Address',
        key: 'address',
        render: (_, record) => record.order_property?.apartment_address || record.delivery_address_1 || ' ',
        ellipsis: true,
        width: 200
      },
      {
        title: 'Post Code',
        key: 'post_code',
        render: (_, record) => record.order_property?.delivery_zip_code || record.delivery_zip_code || ' ',
        width: 120
      },
      {
        title: 'Status',
        dataIndex: 'order_status',
        key: 'order_status',
        render: (status) => {
          if (successStatuses.includes(status)) {
            return <Tag color="success">{status}</Tag>;
          } else if (failedStatuses.includes(status)) {
            return <Tag color="error">{status}</Tag>;
          }
          return <Tag color="processing">{status}</Tag>;
        },
        sorter: (a, b) => a.order_status.localeCompare(b.order_status),
        width: 120
      },
      {
        title: showPricesWithoutVAT ? 'Total (excl. VAT)' : 'Total',
        dataIndex: 'order_total',
        key: 'order_total',
        render: (total) => formatCurrency(total),
        sorter: (a, b) => parseFloat(a.order_total) - parseFloat(b.order_total),
        width: 100,
        align: 'right'
      },
      // VAT column - only shown when prices are without VAT
      ...(showPricesWithoutVAT ? [
        {
          title: 'VAT Amount',
          dataIndex: 'vat_amount',
          key: 'vat_amount',
          render: (_, record) => formatCurrency(record.vat_amount || 0),
          width: 100,
          align: 'right'
        }
      ] : []),
      {
        title: 'Items',
        key: 'items',
        width: 100,
        render: (_, record) => {
          const itemCount = record.order_products ? record.order_products.length : 0;
          return itemCount > 0 ? <Tag color="blue">{itemCount} items</Tag> : '-';
        }
      }
    ];
    
    return columns;
  };

  // Get current configuration based on view mode
  const getDataAndColumns = () => {
    if (viewMode === 'default') {
      return {
        columns: getBaseColumns(),
        dataSource: tableData.dataSource
      };
    } 
    else if (viewMode === 'grouped') {
      const groupedColumns = [
        {
          title: 'Property / Order ID',
          dataIndex: 'property',
          key: 'property',
          render: (text, record) => {
            if (record.isPropertyGroup) {
              return (
                <span className="font-bold">
                  {text} ({record.ordersCount} orders)
                </span>
              );
            }
            return record.id;
          },
          width: 200
        },
        ...getBaseColumns().slice(1, 4), // Date, Time Booked, Customer
        ...getBaseColumns().slice(5, 8), // Address, Post Code, Status
        {
          title: showPricesWithoutVAT ? 'Total (excl. VAT)' : 'Total',
          key: 'order_total',
          render: (_, record) => {
            if (record.isPropertyGroup) {
              return <span className="font-bold">{formatCurrency(record.totalRevenue)}</span>;
            }
            return formatCurrency(record.order_total);
          },
          width: 100,
          align: 'right'
        },
        // VAT column for grouped view
        ...(showPricesWithoutVAT ? [
          {
            title: 'VAT Amount',
            key: 'vat_amount',
            render: (_, record) => {
              if (record.isPropertyGroup) {
                return <span className="font-bold">{formatCurrency(record.totalVAT)}</span>;
              }
              return formatCurrency(record.vat_amount || 0);
            },
            width: 100,
            align: 'right'
          }
        ] : []),
        {
          title: 'Items',
          key: 'items',
          width: 100,
          render: (_, record) => {
            if (record.isPropertyGroup) {
              return <Tag color="blue">{record.itemsCount} items</Tag>;
            }
            const itemCount = record.order_products ? record.order_products.length : 0;
            return itemCount > 0 ? <Tag color="blue">{itemCount} items</Tag> : '-';
          }
        }
      ];

      return {
        columns: groupedColumns,
        dataSource: tableData.dataSource
      };
    } 
    else if (viewMode === 'items') {
      // For items view, we'll use expandable rows to show order items
      return {
        columns: getBaseColumns(),
        dataSource: tableData.dataSource,
        expandable: {
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys(keys),
          expandedRowRender: (record) => {
            const orderItems = record.order_products || [];
            
            if (orderItems.length === 0) {
              return <p className="text-gray-500 italic p-4">No item details available</p>;
            }
            
            const itemColumns = [
              {
                title: 'Item',
                dataIndex: 'order_products_name',
                key: 'item',
                render: (text, item) => text || item.name || 'Unknown Item'
              },
              {
                title: 'Quantity',
                dataIndex: 'order_products_qty',
                key: 'quantity',
                width: 100,
                align: 'center'
              },
              {
                title: showPricesWithoutVAT ? 'Price (excl. VAT)' : 'Price',
                dataIndex: 'order_products_price',
                key: 'price',
                width: 120,
                render: (price) => formatCurrency(price),
                align: 'right'
              },
              // VAT column for item prices
              ...(showPricesWithoutVAT ? [
                {
                  title: 'VAT Amount',
                  key: 'vat_amount',
                  width: 120,
                  render: (_, item) => formatCurrency(item.vat_amount || 0),
                  align: 'right'
                }
              ] : []),
              {
                title: showPricesWithoutVAT ? 'Total (excl. VAT)' : 'Total',
                key: 'total',
                width: 120,
                render: (_, item) => {
                  const price = parseFloat(item.order_products_price || 0);
                  const qty = parseFloat(item.order_products_qty || 0);
                  return formatCurrency(price * qty);
                },
                align: 'right'
              }
            ];
            
            return (
              <div className="p-4 bg-gray-50 rounded-md">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium">Order Items</h4>
                  <div className="text-sm text-gray-500">
                    Items: {orderItems.length} | 
                    {showPricesWithoutVAT 
                      ? ` Order Total (excl. VAT): ${formatCurrency(record.order_total)} | VAT: ${formatCurrency(record.vat_amount || 0)}`
                      : ` Order Total: ${formatCurrency(record.order_total)}`
                    }
                  </div>
                </div>
                <Table 
                  columns={itemColumns}
                  dataSource={orderItems}
                  rowKey={(item) => `item-${item.id || Math.random()}`}
                  pagination={{
                    defaultPageSize: 10,
                    pageSizeOptions: ['10', '25', '50', '100'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`
                  }}
                  size="small"
                  summary={(pageData) => {
                    const itemsTotal = pageData.reduce((sum, item) => {
                      const price = parseFloat(item.order_products_price || 0);
                      const qty = parseFloat(item.order_products_qty || 0);
                      return sum + (price * qty);
                    }, 0);
                    
                    const vatTotal = showPricesWithoutVAT 
                      ? pageData.reduce((sum, item) => {
                          const price = parseFloat(item.order_products_price || 0);
                          const qty = parseFloat(item.order_products_qty || 0);
                          const vat = parseFloat(item.vat_amount || 0);
                          return sum + (vat * qty);
                        }, 0)
                      : 0;
                    
                    return (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={showPricesWithoutVAT ? 2 : 2} className="text-right font-bold">
                            {showPricesWithoutVAT ? 'Subtotal (excl. VAT):' : 'Subtotal:'}
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={1} className="font-bold text-right">
                            {formatCurrency(itemsTotal)}
                          </Table.Summary.Cell>
                          
                          {/* VAT summary for items */}
                          {showPricesWithoutVAT && (
                            <>
                              <Table.Summary.Cell index={2} className="font-bold text-right">
                                {formatCurrency(vatTotal)}
                              </Table.Summary.Cell>
                              <Table.Summary.Cell index={3} className="font-bold text-right">
                                {formatCurrency(itemsTotal + vatTotal)}
                              </Table.Summary.Cell>
                            </>
                          )}
                        </Table.Summary.Row>
                        
                        {/* Show total including VAT if needed */}
                        {showPricesWithoutVAT && (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={2} className="text-right font-bold">
                              Total (incl. VAT):
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={1} className="font-bold text-right">
                              
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={2} className="font-bold text-right">
                              
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} className="font-bold text-right">
                              {formatCurrency(itemsTotal + vatTotal)}
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                      </>
                    );
                  }}
                />
              </div>
            );
          },
          expandIcon: ({ expanded, onExpand, record }) => 
            expanded ? (
              <DownOutlined className="text-blue-500 mr-2" onClick={e => onExpand(record, e)} />
            ) : (
              <RightOutlined className="text-gray-400 mr-2" onClick={e => onExpand(record, e)} />
            )
        }
      };
    }
  };

  // Get table configuration based on current view mode
  const { columns, dataSource, expandable } = getDataAndColumns();

  // Handle view mode changes and notify parent component
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    setExpandedRowKeys([]);
    
    if (onViewModeChange) {
      onViewModeChange(mode);
    }
  };

  return (
    <>
      <Card className="mb-4" size="small">
        <div className="flex flex-wrap items-center justify-between">
          <div className="flex items-center gap-6">
            <Radio.Group 
              value={viewMode} 
              onChange={(e) => handleViewModeChange(e.target.value)}
              optionType="button"
              buttonStyle="solid"
            >
              <Radio.Button value="default">Default View</Radio.Button>
              <Radio.Button value="items">Show Order Items</Radio.Button>
              <Radio.Button value="grouped">Group by Property</Radio.Button>
            </Radio.Group>
            <Tooltip title="Default view shows orders in a flat list. 'Show Order Items' displays detailed item information. 'Group by Property' organizes orders by property.">
              <InfoCircleOutlined className="text-gray-400" />
            </Tooltip>
          </div>
          
        
        </div>
      </Card>
      
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={record => record.isPropertyGroup ? record.key : record.id}
        loading={loading}
        scroll={{ x: 1200 }}
        pagination={{
          defaultPageSize: 25,
          pageSizeOptions: ['25', '50', '100', '200'],
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} ${viewMode === 'grouped' ? 'properties/orders' : 'orders'}`
        }}
       
        summary={tableSummary}
        expandable={expandable}
        className="shadow-sm"
      />
    </>
  );
};

export default OrderDetailsTab;
